import { FC } from "react";
import { FaCircleStop, FaMicrophone, FaRss } from "react-icons/fa6";
import { Message } from "./types";
import { AxiosResponse } from "axios";
import { ApiResponse } from "../../../../../../types/types";
import useOpener from "../../../../../../hooks/useOpener";
import TerminateModal from "../../../../components/modal/TerminateModal";
import CountdownTimer from "./CountdownTimer";
import useFiBiVoiceBottomBar from "./useFiBiVoiceBottomBar";
import { getIsMediaSourceSupported } from "./data";

//
export interface FiBiVoiceBottomBarProps {
  onAddToChatHistory: (value: Message) => void;
  allMessages: Message[];
  businessProps?: {
    onEnd: () => Promise<any>;
    currentSession: string;
    businessId?: string;
    activeSession: string | null;
  };
  normalProps?: {
    onRestart: () => void;
    deleteLoading: boolean;
  };
  audioRef: React.MutableRefObject<HTMLAudioElement>;
  audioContextRef: React.MutableRefObject<AudioContext | null>;
  requestFunc: (
    prompt: string
  ) => Promise<AxiosResponse<ApiResponse<any, any>, any> | undefined>;
  onInterim: (second: string) => void;
}

const FiBiVoiceBottomBar: FC<FiBiVoiceBottomBarProps> = (props) => {
  const {
    isListening,
    stopListening,
    startListening,
    fibiSpeaking,
    fibiProcessing,
    networkStatus,
  } = useFiBiVoiceBottomBar(props);
  const isMediaSourceSupported = getIsMediaSourceSupported();
  const isBusinessProps = !!props.businessProps;
  const { open, onOpen, onClose } = useOpener();

  return (
    <>
      <div className="flex flex-col md:flex-row items-center justify-between w-full p-4 mx-auto bg-blue-100 md:w-[85%] rounded-3xl gap-y-4 md:gap-x-6">
        <div className="flex items-center justify-between w-full">
          <div className="flex flex-col gap-2 font-bold text-gray-800">
            {!isMediaSourceSupported && (
              <div
                className={`${
                  networkStatus === "stable"
                    ? "bg-green-50"
                    : networkStatus === "unstable"
                    ? "bg-yellow-50"
                    : "bg-red-50"
                } flex items-center gap-2 px-3 py-1 rounded-full transition-all duration-300 text-xs`}
              >
                <FaRss
                  className={`${
                    networkStatus === "stable"
                      ? "text-green-500"
                      : networkStatus === "unstable"
                      ? "text-yellow-500"
                      : "text-red-400"
                  } ${networkStatus !== "disconnected" ? "animate-pulse" : ""}`}
                />
                <span
                  className={`${
                    networkStatus === "stable"
                      ? "text-green-500"
                      : networkStatus === "unstable"
                      ? "text-yellow-500"
                      : "text-red-400"
                  }`}
                >
                  Network:{" "}
                  {networkStatus === "stable"
                    ? "Stable"
                    : networkStatus === "unstable"
                    ? "Unstable"
                    : "Disconnected"}
                </span>
              </div>
            )}
            <span className="tracking-widest">
              {isBusinessProps && props.businessProps && (
                <CountdownTimer
                  duration={600}
                  onEnd={props.businessProps.onEnd}
                />
              )}
            </span>
          </div>

          <div className="flex flex-col items-center gap-2">
            {fibiSpeaking || fibiProcessing ? (
              <div className=" text-primaryColor">
                FiBi is {fibiSpeaking ? "speaking" : "processing"}...
              </div>
            ) : (
              <button onClick={isListening ? stopListening : startListening}>
                {isListening ? (
                  <div className="flex items-center justify-center p-2 text-red-500 rounded-lg bg-red-50">
                    <FaCircleStop size={24} />
                  </div>
                ) : (
                  <div className="flex items-center justify-center p-2 text-blue-500 bg-white rounded-lg">
                    <FaMicrophone size={24} />
                  </div>
                )}
              </button>
            )}

            {isBusinessProps && props.businessProps && (
              <div className="text-sm text-gray-800">
                {props.businessProps.currentSession}
              </div>
            )}
          </div>

          {isBusinessProps && props.businessProps && (
            <button className="text-red-500 underline" onClick={onOpen}>
              End session
            </button>
          )}
        </div>
      </div>

      {open && props.businessProps && (
        <TerminateModal
          title="Are you sure you want to end this session?"
          subText="This session will be ended immediately. This action is irreversible."
          onClose={onClose}
          onConfirm={props.businessProps.onEnd}
          isLoading={false}
        />
      )}
    </>
  );
};

export default FiBiVoiceBottomBar;
